/**
 * TaskScribe is monochrome by default — black, white, and a grey ramp, crisp,
 * griddy, structural. The only
 * colour in the product comes from user-selected tags (tag chips keep their
 * chosen hue; nothing else does). `palette` holds the fixed monochrome anchors.
 */
export const palette = {
  ink: "#000000",
  paper: "#FFFFFF",
  // Legacy aliases (kept for existing references; all resolve to greyscale).
  brandInk: "#000000",
  brandPaper: "#FFFFFF",
  brandCoral: "#6B6B6B",
  brandSage: "#6B6B6B",
  brandRule: "#E2E2E2",
  brandMidnight: "#000000",
  brandMarigold: "#6B6B6B",
  brandCeleste: "#E2E2E2",
  brandHerb: "#6B6B6B",
  brandLionsmane: "#FFFFFF",
} as const;

export const spacing = {
  xs: 4,
  s: 8,
  m: 12,
  l: 16,
  xl: 24,
  xxl: 32,
  xxxl: 48,
  huge: 64,
} as const;

/** Sharp corners — the grid does the work, not rounding. `pill` stays for
 *  tag chips. */
export const radii = {
  xs: 2,
  s: 4,
  m: 6,
  l: 8,
  xl: 999,
  control: 4,
  card: 4,
  pill: 999,
} as const;

// Fira Sans is a humanist sans — open apertures, calligraphic skeleton — which
// suits a writing/task product far better than the serif this used to carry.
// There is no second voice: `display` is the same family set heavier, so the
// grid and the rules carry the design (see the monochrome direction in
// DESIGN notes). Fira Mono is a real monospace, replacing an alias to the sans.
export const fonts = {
  regular: "FiraSans_400Regular",
  medium: "FiraSans_500Medium",
  semibold: "FiraSans_600SemiBold",
  bold: "FiraSans_700Bold",
  display: "FiraSans_700Bold",
  displaySemibold: "FiraSans_600SemiBold",
  mono: "FiraMono_400Regular",
  monoMedium: "FiraMono_500Medium",
  monoBold: "FiraMono_700Bold",
} as const;

/**
 * One type scale for the whole app. Screens and primitives should pull from
 * here instead of hardcoding `fontSize` / `lineHeight` / `fontFamily`.
 */
export const typography = {
  display1: { fontFamily: fonts.display, fontSize: 30, lineHeight: 36 },
  display2: { fontFamily: fonts.displaySemibold, fontSize: 24, lineHeight: 30 },
  large: { fontFamily: fonts.regular, fontSize: 17, lineHeight: 26 },
  body: { fontFamily: fonts.regular, fontSize: 15, lineHeight: 22 },
  small: { fontFamily: fonts.regular, fontSize: 13, lineHeight: 19 },
  xs: { fontFamily: fonts.regular, fontSize: 12, lineHeight: 17 },
  label: { fontFamily: fonts.medium, fontSize: 13, lineHeight: 18 },
} as const;

/** Elevation is barely used — borders carry the structure. Reserved for
 *  modals / menus. */
export const shadows = {
  sm: {
    shadowColor: palette.ink,
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.04,
    shadowRadius: 1,
    elevation: 1,
  },
  md: {
    shadowColor: palette.ink,
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.1,
    shadowRadius: 20,
    elevation: 6,
  },
} as const;

export type ColorScheme = "light" | "dark";

/** Semantic colour contract — key names shared across the app fleet. Every
 *  value here is greyscale; product colour lives on tags only. */
export type ThemeColors = {
  background: string;
  elevated: string;
  surface: string;
  mutedSurface: string;
  muted: string;
  foreground: string;
  heading: string;
  mutedForeground: string;
  primary: string;
  primaryHover: string;
  primaryForeground: string;
  primarySurface: string;
  secondary: string;
  secondaryForeground: string;
  accent: string;
  danger: string;
  dangerSurface: string;
  warning: string;
  success: string;
  successSurface: string;
  border: string;
  borderStrong: string;
  focusRing: string;
  overlay: string;
};

export const lightColors: ThemeColors = {
  background: "#FFFFFF",
  elevated: "#FFFFFF",
  surface: "#F5F5F5",
  mutedSurface: "#EDEDED",
  muted: "#F5F5F5",
  foreground: "#1A1A1A",
  heading: "#000000",
  mutedForeground: "#6B6B6B",
  primary: "#1A1A1A",
  primaryHover: "#000000",
  primaryForeground: "#FFFFFF",
  primarySurface: "#F0F0F0",
  secondary: "#F5F5F5",
  secondaryForeground: "#1A1A1A",
  accent: "#1A1A1A",
  danger: "#1A1A1A",
  dangerSurface: "#F0F0F0",
  warning: "#1A1A1A",
  success: "#1A1A1A",
  successSurface: "#F0F0F0",
  border: "#E2E2E2",
  borderStrong: "#1A1A1A",
  focusRing: "rgba(0, 0, 0, 0.35)",
  overlay: "rgba(0, 0, 0, 0.55)",
};

/** Dark mode is a straight inversion of the grey ramp. */
export const darkColors: ThemeColors = {
  background: "#0B0B0B",
  elevated: "#141414",
  surface: "#181818",
  mutedSurface: "#1F1F1F",
  muted: "#181818",
  foreground: "#EDEDED",
  heading: "#FFFFFF",
  mutedForeground: "#9A9A9A",
  primary: "#EDEDED",
  primaryHover: "#FFFFFF",
  primaryForeground: "#0B0B0B",
  primarySurface: "#1F1F1F",
  secondary: "#181818",
  secondaryForeground: "#EDEDED",
  accent: "#EDEDED",
  danger: "#EDEDED",
  dangerSurface: "#1F1F1F",
  warning: "#EDEDED",
  success: "#EDEDED",
  successSurface: "#1F1F1F",
  border: "#2C2C2C",
  borderStrong: "#EDEDED",
  focusRing: "rgba(255, 255, 255, 0.4)",
  overlay: "rgba(0, 0, 0, 0.7)",
};

export const tokens = {
  palette,
  spacing,
  radii,
  fonts,
  typography,
  shadows,
  lightColors,
  darkColors,
} as const;
